import React, {
	type DragEvent as ReactDragEvent,
	useRef,
	useCallback,
	useMemo,
	type MouseEvent,
} from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import { ScrollTrack, ScrollThumb } from '@atlassian/jira-portfolio-3-common/src/custom-scrollbar';
import { usePayload } from '../../controllers';
import { useScrollAnalytics } from './scroll-analytics';

interface Props {
	name: string;
}

// empty image gif
const getEmptyImage = () => {
	const emptyImage = document.createElement('img');
	emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

	return emptyImage;
};

export const getOffsetFromTrackPosition = ({
	trackX,
	trackWidth,
	thumbWidth,
	mouseX,
	scrollWidth,
}: {
	trackX: number;
	trackWidth: number;
	thumbWidth: number;
	mouseX: number;
	scrollWidth: number;
}): number => {
	const factor = scrollWidth / trackWidth;
	const relativePosition = mouseX - trackX;
	return Math.round(relativePosition * factor - (thumbWidth * factor) / 2);
};

export const HorizontalSyncedScrollbar = ({ name }: Props) => {
	const [{ offset, viewportWidth, scrollWidth }, { setOffset }] = usePayload(name);

	// When cleaning up prevent-nan-errors-on-hz-scrolling-bar_ikqgn, refactor to
	// const scale = scrollWidth ? viewportWidth / scrollWidth : 1;
	const scalePreventNaN = scrollWidth ? viewportWidth / scrollWidth : 1;

	const scale = ff('prevent-nan-errors-on-hz-scrolling-bar_ikqgn')
		? scalePreventNaN
		: viewportWidth / scrollWidth;

	const offsetRef = useRef(offset);
	offsetRef.current = offset;

	const startingOffsetRef = useRef(offset);
	const startingClientXRef = useRef(0);

	const thumbWidth = viewportWidth * scale;
	const emptyImage = useMemo(() => getEmptyImage(), []);

	const handleDrag = useCallback(
		(e: DragEvent) => {
			const { clientX } = e;

			if (clientX === 0) {
				return;
			}
			requestAnimationFrame(() => {
				// eslint-disable-next-line @typescript-eslint/no-shadow
				const offset = Math.round(
					startingOffsetRef.current + (clientX - startingClientXRef.current) / scale,
				);
				setOffset(name, offset);
			});
		},
		[scale, name, setOffset],
	);

	const handleDragStart = useCallback(
		(e: ReactDragEvent<HTMLDivElement>) => {
			e.nativeEvent.dataTransfer?.setDragImage(emptyImage, 0, 0);

			startingClientXRef.current = e.clientX;
			startingOffsetRef.current = offsetRef.current;

			// eslint-disable-next-line @atlaskit/design-system/no-direct-use-of-web-platform-drag-and-drop
			document.body.addEventListener('dragover', handleDrag);

			e.stopPropagation();
		},
		[handleDrag, emptyImage],
	);

	const handleDragEnd = useCallback(() => {
		document.body.removeEventListener('dragover', handleDrag);
	}, [handleDrag]);

	const handleClick = useCallback(
		(event: MouseEvent) => {
			const { left, width } = event.currentTarget.getBoundingClientRect();

			// eslint-disable-next-line @typescript-eslint/no-shadow
			const offset = getOffsetFromTrackPosition({
				trackX: left,
				trackWidth: width,
				thumbWidth,
				mouseX: event.clientX,
				scrollWidth,
			});
			setOffset(name, offset);
		},
		[thumbWidth, name, setOffset, scrollWidth],
	);

	useScrollAnalytics(offset);

	if (scale === 1) {
		return null;
	}

	return (
		<ScrollTrack
			onClick={handleClick}
			role="scrollbar"
			data-testid="portfolio-3-horizontal-scrolling-synced.ui.scrollbar.scroll-track"
		>
			<ScrollThumb
				draggable
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				width={thumbWidth}
				left={offset * scale}
				data-testid="portfolio-3-horizontal-scrolling-synced.ui.scrollbar.scroll-thumb"
			/>
		</ScrollTrack>
	);
};
